<template>
  <!-- left button (decrement) -->
  <div>
    <div class="d-flex align-items-center">
      <button
        type="button"
        class="btn btn-lg btn-warning float-right"
        @click="decrement"
        :disabled="disabled"
      >
        -
      </button>
      <div class="col form-group ms-2 me-2">
        <div class="form-floating">
          <input
            class="form-control"
            style="text-align: center"
            v-model="return_count"
            v-bind:class="{
              'is-invalid': error || invalid,
              'bb-warning': isMax,
            }"
            autocomplete="off"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="onKeyDown($event)"
            @keyup="onKeyUp($event)"
            :id="pass_ref"
            :ref="pass_ref"
            :maxlength="pass_digits"
            :disabled="disabled"
          />
          <label
            v-if="pass_optional"
            class="optional bb-float-label"
            :class="{ 'bb-warning': isMax }"
            for="pass_ref"
            >{{ this.pass_label }}</label
          >
          <label
            v-else
            class="bb-float-label"
            :class="{ 'bb-warning': isMax }"
            for="pass_ref"
            >{{ this.pass_label }}</label
          >
        </div>
      </div>
      <button
        type="button"
        class="btn btn-lg btn-primary float-left"
        @click="increment"
        :disabled="disabled"
      >
        +
      </button>
    </div>
    <!-- ------------ -->
    <!--    errors    -->
    <!-- ------------ -->
    <div
      v-if="error"
      class="row mt-3 bb-error justify-content-center"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
//----------------------
// scripts
//----------------------
export default {
  name: 'CounterEmit',
  emits: ['change'],
  //----------------------
  // props
  //----------------------
  props: {
    pass_label: {
      type: String,
      required: true,
    },
    pass_count: {
      type: Number,
      required: true,
    },
    pass_min: {
      type: Number,
      required: true,
    },
    pass_max: {
      type: Number,
      required: true,
    },
    pass_ref: {
      type: String,
      required: true,
    },
    pass_digits: {
      type: Number,
      required: true,
    },
    pass_optional: {
      type: Boolean,
      required: false,
      default: false,
    },
    pass_skip_zero: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    invalid: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  //----------------------
  // data
  //----------------------
  data() {
    return {
      error: '',
      return_count: null,
      hasPendingInvalidInput: false,
      isEditing: false,
    }
  },
  //----------------------
  // computed
  //----------------------
  computed: {
    isMax() {
      return this.return_count == this.pass_max
    },
  },
  //----------------------
  // methods
  //----------------------
  methods: {
    //----------------------------
    // build invalid range message
    //----------------------------
    getInvalidRangeMessage() {
      return (
        'Invalid ' +
        this.pass_label +
        ' (Valid range: ' +
        this.pass_min +
        '-' +
        this.pass_max +
        ')'
      )
    },
    //----------------------------
    // focus / blur
    //----------------------------
    onFocus() {
      this.isEditing = true
    },
    onBlur() {
      this.isEditing = false
      this.syncValidation()
    },
    //----------------------------
    // sync validation
    //----------------------------
    syncValidation() {
      const currentValue =
        this.hasPendingInvalidInput || this.isEditing
          ? this.return_count
          : this.pass_count

      this.return_count = currentValue

      if (
        this.return_count == null ||
        this.return_count === '' ||
        Number.isNaN(Number(this.return_count))
      ) {
        this.error = ''
        this.hasPendingInvalidInput = false
        return
      }

      const numericValue = Number(this.return_count)
      if (numericValue < this.pass_min || numericValue > this.pass_max) {
        this.error = this.getInvalidRangeMessage()
        this.hasPendingInvalidInput = true
        return
      }

      this.error = ''
      if (this.hasPendingInvalidInput && numericValue !== this.pass_count) {
        this.$emit('change', { ref: this.pass_ref, count: numericValue })
      }
      this.hasPendingInvalidInput = false
    },
    //----------------------------
    // setCount (external control)
    //----------------------------
    setCount(newCount) {
      // update internal value without emitting a change event
      this.return_count = newCount
      this.error = ''
      this.hasPendingInvalidInput = false
    },
    //----------------------------
    // increment
    //----------------------------
    increment() {
      this.return_count = this.pass_count + 1
      if (this.return_count > this.pass_max) this.return_count = this.pass_min
      if (this.pass_skip_zero && this.return_count == 0)
        this.return_count = this.pass_min
      this.error = ''
      this.hasPendingInvalidInput = false
      this.$emit('change', { ref: this.pass_ref, count: this.return_count })
    },
    //----------------------------
    // decrement
    //----------------------------
    decrement() {
      let return_count = this.pass_count - 1
      if (return_count < this.pass_min) return_count = this.pass_max
      if (this.pass_skip_zero && return_count == 0) {
        return_count = -1
        if (return_count < this.pass_min) return
      }
      this.return_count = return_count
      this.error = ''
      this.hasPendingInvalidInput = false
      this.$emit('change', { ref: this.pass_ref, count: return_count })
    },
    //----------------------------
    // clear
    //----------------------------
    clear(e) {
      this.error = ''
      this.return_count = 0
      this.hasPendingInvalidInput = false
      e.target.value = ''
    },
    //----------------------------
    // onKeyDown
    //----------------------------
    onKeyDown(e) {
      if (!this.isNumber(e)) {
        e.preventDefault()
      }
      if (this.pass_digits == 1) {
        if (!this.isSingleDigitRange(e, this.pass_min, this.pass_max)) {
          e.preventDefault()
        }
      }
    },
    //----------------------------
    // onKeyUp
    //----------------------------
    onKeyUp(e) {
      const rawValue = e.target.value

      if (rawValue === '') {
        this.return_count = ''
        this.error = ''
        this.hasPendingInvalidInput = false
        return
      }

      const numericValue = Number(rawValue)
      if (Number.isNaN(numericValue)) {
        return
      }

      this.return_count = rawValue
      this.$emit('change', { ref: this.pass_ref, count: numericValue })

      if (!this.isRangeValue(numericValue, this.pass_min, this.pass_max)) {
        this.error = this.getInvalidRangeMessage()
        this.hasPendingInvalidInput = true
        e.preventDefault()
        return
      }

      this.error = ''
      this.hasPendingInvalidInput = false
    },
    //----------------------------
    // isNumber
    //----------------------------
    isNumber(e) {
      let num = String.fromCharCode(e.keyCode)
      if (/^[0-9\b]+$/.test(num)) return true
      else return false
    },
    //----------------------------
    // isSingleDigitRange
    //----------------------------
    isSingleDigitRange(e, min, max) {
      let num = String.fromCharCode(e.keyCode)
      const regex = new RegExp('[' + min + '-' + max + '\b' + ']')

      if (regex.test(num)) {
        return true
      } else {
        return false
      }
    },
    //----------------------------
    // isRange
    //----------------------------
    isRange(e, min, max) {
      const value = e.target.value

      if (value < min || value > max) {
        return false
      } else {
        return true
      }
    },
    //----------------------------
    // isRangeValue
    //----------------------------
    isRangeValue(value, min, max) {
      if (value < min || value > max) {
        return false
      } else {
        return true
      }
    },
  },
  //----------------------------
  // watch
  //----------------------------
  watch: {
    pass_count() {
      if (!this.hasPendingInvalidInput && !this.isEditing) {
        this.syncValidation()
      }
    },
    pass_max() {
      this.syncValidation()
    },
    pass_min() {
      this.syncValidation()
    },
  },
  //----------------------
  // mounted
  //----------------------
  mounted() {
    this.syncValidation()
  },
}
</script>
